import { 
  Loader2, Film, Play
} from "lucide-react";
import { getAssetUrl } from "@/utils/studioUtils";

/**
 * AnimateStage - Animation Review - View and select animation takes
 */
export function AnimateStage({
  scene,
  generating,
  animationTakes,
  activeAnimationIndex,
  onSelectAnimationTake
}) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-4" data-testid="animate-stage">
      {/* Loading State - Only when generating first take (no existing takes) */}
      {(generating || scene?.status === "generating") && animationTakes.length === 0 && (
        <div 
          className="relative rounded-lg border border-zinc-800 overflow-hidden bg-zinc-950"
          style={{ 
            aspectRatio: "16/9",
            width: '100%',
            maxWidth: 'calc((100vh - 200px) * 16 / 9)',
            maxHeight: 'calc(100vh - 200px)'
          }}
        >
          {scene?.composite_image_url && (
            <img 
              src={getAssetUrl(scene.composite_image_url)} 
              alt="Shot Preview" 
              className="w-full h-full object-contain opacity-50"
            />
          )}
          <div className="absolute inset-0 flex items-center justify-center bg-zinc-900/70">
            <div className="text-center">
              <Loader2 className="w-16 h-16 mx-auto mb-4 text-indigo-500 animate-spin" />
              <p className="text-white font-medium">Generating Animation...</p>
              <p className="text-sm text-zinc-400 mt-1">This may take 2-5 minutes</p>
            </div>
          </div>
        </div>
      )}

      {/* Takes Carousel (show even when generating if there are existing takes) */}
      {animationTakes.length > 0 && (
        <div className="flex-1 flex flex-col items-center overflow-auto">
          {/* Video Display - Shows active take or generating overlay */}
          <div 
            className="relative rounded-lg border border-zinc-800 overflow-hidden bg-zinc-950 mb-4 shrink-0"
            style={{ 
              aspectRatio: "16/9",
              width: '100%',
              maxWidth: 'min(900px, calc((100vh - 320px) * 16 / 9))',
              maxHeight: 'calc(100vh - 320px)'
            }}
          >
            {animationTakes[activeAnimationIndex] && !generating && scene?.status !== "generating" && (
              <video
                key={animationTakes[activeAnimationIndex].video_url}
                src={getAssetUrl(animationTakes[activeAnimationIndex].video_url)}
                controls
                autoPlay
                loop
                className="w-full h-full object-contain"
              />
            )}
            {/* Show generating overlay when creating new take */}
            {(generating || scene?.status === "generating") && (
              <>
                {scene?.composite_image_url && (
                  <img 
                    src={getAssetUrl(scene.composite_image_url)} 
                    alt="Shot Preview" 
                    className="w-full h-full object-contain opacity-30"
                  />
                )}
                <div className="absolute inset-0 flex items-center justify-center bg-zinc-900/70">
                  <div className="text-center">
                    <Loader2 className="w-12 h-12 mx-auto mb-3 text-indigo-500 animate-spin" />
                    <p className="text-white font-medium">Generating New Take...</p>
                    <p className="text-sm text-zinc-400 mt-1">This may take 2-5 minutes</p>
                    <p className="text-xs text-indigo-400 mt-2">Take {animationTakes.length + 1} will appear below</p>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Takes Carousel Below Video */}
          <div className="w-full max-w-4xl shrink-0 pb-4">
            <div className="flex items-center justify-between mb-2 px-2">
              <span className="text-sm text-zinc-400">
                {(generating || scene?.status === "generating") 
                  ? `${animationTakes.length} take${animationTakes.length !== 1 ? 's' : ''} + 1 generating...`
                  : `Select a take (${animationTakes.length} available)`
                }
              </span>
              {!generating && scene?.status !== "generating" && (
                <span className="text-xs text-indigo-400">Viewing Take {activeAnimationIndex + 1}</span>
              )}
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 px-2">
              {animationTakes.map((take, index) => (
                <button
                  key={take.id || index}
                  onClick={() => onSelectAnimationTake(index)}
                  data-testid={`animation-take-${index}`}
                  disabled={generating || scene?.status === "generating"}
                  className={`flex-shrink-0 w-32 rounded-lg overflow-hidden transition-all
                    ${index === activeAnimationIndex && !generating && scene?.status !== "generating"
                      ? "ring-2 ring-indigo-500 shadow-lg shadow-indigo-500/20" 
                      : "ring-1 ring-zinc-700 hover:ring-zinc-600 opacity-70 hover:opacity-100"
                    }
                    ${(generating || scene?.status === "generating") ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <div className="aspect-video bg-zinc-800 relative flex items-center justify-center">
                    <Play className="w-6 h-6 text-zinc-500" />
                    {index === activeAnimationIndex && !generating && scene?.status !== "generating" && (
                      <div className="absolute top-1 left-1 bg-indigo-600 text-white text-[9px] px-1.5 py-0.5 rounded font-medium">
                        Active
                      </div>
                    )}
                  </div>
                  <div className="p-1.5 bg-zinc-800/50 text-center">
                    <span className="text-xs font-medium text-zinc-200">Take {index + 1}</span>
                  </div>
                </button>
              ))}
              
              {/* Pending Take Placeholder - Shows when generating new take */}
              {(generating || scene?.status === "generating") && (
                <div className="flex-shrink-0 w-32 rounded-lg overflow-hidden ring-2 ring-indigo-500/50 ring-dashed animate-pulse">
                  <div className="aspect-video bg-indigo-500/10 relative flex items-center justify-center">
                    <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
                  </div>
                  <div className="p-1.5 bg-indigo-500/10 text-center">
                    <span className="text-xs font-medium text-indigo-300">Take {animationTakes.length + 1}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* No Takes Yet */}
      {!generating && scene?.status !== "generating" && animationTakes.length === 0 && (
        <div 
          className="relative rounded-lg border border-zinc-800 overflow-hidden bg-zinc-950"
          style={{ 
            aspectRatio: "16/9",
            width: '100%',
            maxWidth: 'calc((100vh - 200px) * 16 / 9)',
            maxHeight: 'calc(100vh - 200px)'
          }}
        >
          {scene?.composite_image_url ? (
            <img 
              src={getAssetUrl(scene.composite_image_url)} 
              alt="Shot Preview" 
              className="w-full h-full object-contain opacity-50"
            />
          ) : null}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-zinc-500">
              <Film className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No animations yet</p>
              <p className="text-xs mt-1">Click &quot;Create Animation&quot; in Preview to create your first take</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnimateStage;
